import { CommonModule } from '@angular/common';
import {
    ChangeDetectorRef,
    Component,
    EventEmitter,
    Input,
    Output
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { AutoCompleteModule } from 'primeng/autocomplete';
import { CheckboxModule } from 'primeng/checkbox';
import { InputTextModule } from 'primeng/inputtext';

import {
    isMultiValueInput,
    WebVisibilityValueInputType
} from '@/interfaces/web-visibility-rule';
import { RatesService } from '@/service/rates.service';

const AUTOCOMPLETE_FIELD_ALIASES: Record<string, string> = {
    rate_unit: 'rate_units',
    effective_rate_unit: 'rate_units',
    district_name: 'district_names',
    provider_name: 'agency_names',
    provider_type: 'provider_types',
};

@Component({
    selector: 'app-condition-value-input',
    standalone: true,
    imports: [CommonModule, FormsModule, AutoCompleteModule, CheckboxModule, InputTextModule],
    template: `
        @switch (valueInputType) {
            @case ('checkbox') {
                <div class="flex items-center gap-2">
                    <p-checkbox [(ngModel)]="checked"
                                [binary]="true"
                                [disabled]="disabled"
                                (ngModelChange)="onCheckboxChange($event)"/>
                    <span class="text-sm">{{ checked ? 'True' : 'False' }}</span>
                </div>
            }
            @case ('text') {
                <input pInputText
                       class="w-full"
                       [ngModel]="singleValue"
                       (ngModelChange)="onSingleValueChange($event)"
                       [disabled]="disabled"
                       placeholder="Enter value"/>
            }
            @case ('list') {
                <div class="flex flex-col gap-2 w-full">
                    @if (values.length) {
                        <div class="flex flex-wrap gap-1">
                            @for (item of values; track item) {
                                <span class="inline-flex items-center gap-1 px-2 py-0.5 text-sm border border-surface-200 rounded-md bg-surface-50">
                                    {{ item }}
                                    @if (!disabled) {
                                        <button type="button"
                                                class="border-none bg-transparent cursor-pointer text-muted-color leading-none p-0"
                                                (click)="removeListValue(item)"
                                                aria-label="Remove value">
                                            ×
                                        </button>
                                    }
                                </span>
                            }
                        </div>
                    }

                    @if (hasAutocompleteField) {
                        <p-autoComplete
                            [(ngModel)]="listDraft"
                            [suggestions]="suggestions"
                            (completeMethod)="onAutocomplete($event)"
                            (onSelect)="addListValue($event.value)"
                            (keydown.enter)="onListEnter($event)"
                            [disabled]="disabled"
                            [forceSelection]="false"
                            [completeOnFocus]="true"
                            [fluid]="true"
                            [delay]="400"
                            [minLength]="2"
                            appendTo="body"
                            class="w-full"
                            placeholder="Search or type a value and press Enter">
                        </p-autoComplete>
                    } @else {
                        <input pInputText
                               class="w-full"
                               [(ngModel)]="listDraft"
                               [disabled]="disabled"
                               (keydown.enter)="onListEnter($event)"
                               placeholder="Type a value and press Enter"/>
                    }
                </div>
            }
        }
    `
})
export class ConditionValueInputComponent {
    @Input() autocompleteField?: string | null;
    @Input() valueInputType: WebVisibilityValueInputType = 'text';
    @Input() values: string[] = [];
    @Input() disabled = false;

    @Output() valuesChange = new EventEmitter<string[]>();

    suggestions: string[] = [];
    listDraft = '';

    constructor(
        private ratesService: RatesService,
        private cdr: ChangeDetectorRef,
    ) {
    }

    get hasAutocompleteField(): boolean {
        return !!this.resolveAutocompleteField();
    }

    get singleValue(): string {
        return this.values?.[0] ?? '';
    }

    get checked(): boolean {
        return this.values?.[0] === 'true';
    }

    set checked(value: boolean) {
        this.values = [value ? 'true' : 'false'];
    }

    onCheckboxChange(value: boolean): void {
        this.valuesChange.emit([value ? 'true' : 'false']);
    }

    onSingleValueChange(value: string): void {
        this.valuesChange.emit(value ? [value] : []);
    }

    onListEnter(event: Event): void {
        event.preventDefault();
        this.addListValue(this.listDraft);
    }

    addListValue(raw?: string | null): void {
        const value = (raw ?? this.listDraft ?? '').trim();
        if (!value || this.values.includes(value)) {
            this.listDraft = '';
            return;
        }

        this.valuesChange.emit([...this.values, value]);
        this.listDraft = '';
        this.suggestions = [];
    }

    removeListValue(value: string): void {
        this.valuesChange.emit(this.values.filter((item) => item !== value));
    }

    onAutocomplete(event: { query: string }): void {
        if (!isMultiValueInput(this.valueInputType)) {
            this.suggestions = [];
            return;
        }

        const field = this.resolveAutocompleteField();
        if (!field) {
            this.suggestions = [];
            return;
        }

        this.ratesService.autocomplete(field, event.query).subscribe({
            next: (options) => {
                this.suggestions = options.filter((option: string) => !this.values.includes(option));
                this.cdr.markForCheck();
            },
            error: () => {
                this.suggestions = [];
                this.cdr.markForCheck();
            }
        });
    }

    private resolveAutocompleteField(): string | null {
        const raw = this.autocompleteField?.trim();
        if (!raw) {
            return null;
        }
        return AUTOCOMPLETE_FIELD_ALIASES[raw] ?? raw;
    }
}
